import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import ReactDatePicker from "react-datepicker";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FilePond } from "react-filepond";
import organizationData from "../../../utils/organizationData.json";
 import { UserSalesSubmitData } from "../../../types/sales/Sales";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { validationSchema } from "./validationDevelopers";
import Breadcrumbs from "../../../common-elements/breadcrumbs/Breadcrumbs";

const generatePassword = () => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
  let password = "";
  for (let i = 0; i < 12; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

const AddDevelopersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [userSalesData, setUserSalesData] = useState<any[]>([]);
  const [password, setPassword] = useState("");
  const [files, setFiles] = useState<any[]>([]);
  const [copyTooltip, setCopyTooltip] = useState("");
  const [passwordOption, setPasswordOption] = useState("setOwn");

  const isEditMode = location.pathname.includes("/edit/");
  const breadcrumbTitle = isEditMode ? t("EditDeveloperUser") : t("AddDeveloperUser");
  const saveButtonText = isEditMode ? t("Modify") : t("Save");

  const passwordInputRef = useRef<any>(null);

  const initialValues: any = {
    firstName: "",
    lastName: "",
    email: "",
    dob: null,
    nationality: ["United Kingdom"],
    mobileCountryCode: "+44",
    mobile: "",
    gender: "",
    image: "",
    emailVerification: false,
    mobileVerification: false,
    passwordResetRequired: false,
    theme: "",
    language: "en",
    password: "",
    passwordOption: "setOwn",
  };

  const handleSubmit = (values: any) => {
    if (passwordOption === "setOwn" && !values.password) {
      alert("Password is required when setting custom password");
      return;
    }

    const submittedData: UserSalesSubmitData = {
      user_first_name: values.firstName,
      user_last_name: values.lastName,
      user_email: values.email,
      user_dob: values.dob,
      nationality: values.nationality,
      user_mobile_country_code: values.mobileCountryCode,
      user_mobile: values.mobile,
      user_password: password || values.password,
      user_gender: values.gender,
      user_image: values.image,
      user_email_verification: values.emailVerification,
      user_mobile_verification: values.mobileVerification,
      user_password_reset_required: values.passwordResetRequired,
      user_theme: values.theme,
      user_language: values.language,
    };

    setUserSalesData([...userSalesData, submittedData]);
    navigate(`${process.env.PUBLIC_URL}/users/developers`);
  };

  const handleClickCancel = () => {
    navigate(`${process.env.PUBLIC_URL}/users/developers`);
  };

  const handlePasswordOptionChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    setFieldValue: any
  ) => {
    const value = e.target.value;
    setPasswordOption(value);
    setFieldValue("passwordOption", value);
    if (value === "autoGenerate") {
      const newPassword = generatePassword();
      setPassword(newPassword);
      setFieldValue("password", newPassword);
    } else if (value === "setOwn") {
      setPassword("");
      setFieldValue("password", "");
    } else if (value === "invitationMail") {
      setPassword("");
      setFieldValue("password", "");
    }
  };

  const nationalityOptions =
    organizationData.countries.map((country) => ({
      name: country.label,
      value: country.value,
      flag: country.flag,
    })) || [];

  const genderOptions = [
    { name: t("Male"), value: "Male", flag: "" },
    { name: t("Female"), value: "Female", flag: "" },
    { name: t("Other"), value: "Other", flag: "" },
  ];

  const languageOptions = [
    { name: "English", value: "en", flag: "gb" },
    { name: "French", value: "fr", flag: "fr" },
  ];

  const countryCodeOptions = [
    { name: "+1", value: "+1", flag: "us" },
    { name: "+91", value: "+91", flag: "in" },
    { name: "+44", value: "+44", flag: "gb" },
  ];

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(password);
    setCopyTooltip(t("Copied"));
    setTimeout(() => setCopyTooltip(""), 2000);
  };

  // Option type for Typeahead
  interface FlagOption {
    name: string;
    value: string;
    flag?: string;
  }

  return (
    <>
      <Breadcrumbs
        mainTitle={breadcrumbTitle}
        parents={[
          { label: t("Users"), link: `${process.env.PUBLIC_URL}/users/developers` },
          { label: t("Developers"), link: `${process.env.PUBLIC_URL}/users/developers` },
        ]}
      />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ setFieldValue, values }) => (
                    <Form className="theme-form">
                      <Row className="g-3">
                        <Col sm="6">
                          <FormGroup>
                            <Label for="firstName" check>
                              {t("FirstName")}{" "}
                              <span className="txt-danger">*</span>
                            </Label>
                            <Field
                              id="firstName"
                              name="firstName"
                              type="text"
                              className="form-control"
                              placeholder={t("EnterFirstName")}
                            />
                            <ErrorMessage
                              name="firstName"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm="6">
                          <FormGroup>
                            <Label for="lastName" check>
                              {t("LastName")}
                            </Label>
                            <Field
                              id="lastName"
                              name="lastName"
                              type="text"
                              className="form-control"
                              placeholder={t("EnterLastName")}
                            />
                            <ErrorMessage
                              name="lastName"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-3">
                        <Col sm="6">
                          <FormGroup>
                            <Label for="email" check>
                              {t("Email")}{" "}
                              <span className="txt-danger">*</span>
                            </Label>
                            <Field
                              id="email"
                              name="email"
                              type="email"
                              className="form-control"
                              placeholder={t("EnterEmail")}
                            />
                            <ErrorMessage
                              name="email"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm="6">
                          <FormGroup>
                            <Label for="mobileCountryCode" check>
                              {t("MobileNumber")} <span className="txt-danger">*</span>
                            </Label>
                            <Row className="g-2">
                              <Col sm="4">
                                <Typeahead
                                  id="country-code-typeahead"
                                  labelKey="name"
                                  options={countryCodeOptions as FlagOption[]}
                                  placeholder={t("Select Code")}
                                  selected={countryCodeOptions.filter(opt => opt.value === values.mobileCountryCode) as FlagOption[]}
                                  onChange={selected => {
                                    const sel = selected[0];
                                    if (sel && typeof sel === 'object' && 'value' in sel) {
                                      setFieldValue("mobileCountryCode", sel.value);
                                    } else {
                                      setFieldValue("mobileCountryCode", "");
                                    }
                                  }}
                                  renderMenuItemChildren={option => {
                                    const opt = option as FlagOption;
                                    return (
                                      <div className="d-flex align-items-center">
                                        {opt.flag && (
                                          <img
                                            src={`/assets/fonts/flag-icon/${opt.flag}.svg`}
                                            alt={opt.name}
                                            style={{ width: 20, height: 15, marginRight: 8 }}
                                          />
                                        )}
                                        <span>{opt.name}</span>
                                      </div>
                                    );
                                  }}
                                  renderToken={(option, props) => {
                                    const opt = option as FlagOption;
                                    return (
                                      <span {...props}>
                                        {opt.flag && (
                                          <img
                                            src={`/assets/fonts/flag-icon/${opt.flag}.svg`}
                                            alt={opt.name}
                                            style={{ width: 20, height: 15, marginRight: 4 }}
                                          />
                                        )}
                                        {opt.name}
                                      </span>
                                    );
                                  }}
                                  renderInput={({ inputRef, referenceElementRef, ...inputProps }) => {
                                    const selected = countryCodeOptions.find(opt => opt.value === values.mobileCountryCode);
                                    return (
                                      <div style={{ position: "relative" }}>
                                        {selected?.flag && (
                                          <img
                                            src={`/assets/fonts/flag-icon/${selected.flag}.svg`}
                                            alt={selected.name}
                                            style={{
                                              position: "absolute",
                                              left: 10,
                                              top: "50%",
                                              transform: "translateY(-50%)",
                                              width: 20,
                                              height: 15,
                                              pointerEvents: "none",
                                              zIndex: 2
                                            }}
                                          />
                                        )}
                                        <input
                                          {...inputProps}
                                          ref={input => {
                                            inputRef(input);
                                            referenceElementRef(input);
                                          }}
                                          className="form-control"
                                          style={{ paddingLeft: selected?.flag ? 35 : 12 }}
                                        />
                                      </div>
                                    );
                                  }}
                                  multiple={false}
                                />
                                <ErrorMessage
                                  name="mobileCountryCode"
                                  component="span"
                                  className="text-danger"
                                />
                              </Col>
                              <Col sm="8">
                                <Field
                                  id="mobile"
                                  name="mobile"
                                  type="text"
                                  className="form-control"
                                  placeholder={t("EnterMobileNumber")}
                                />
                                <ErrorMessage
                                  name="mobile"
                                  component="span"
                                  className="text-danger"
                                />
                              </Col>
                            </Row>
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-3">
                        <Col sm="6">
                          <FormGroup>
                            <Label for="passwordOption" check>
                              {t("Password Option")}{" "}
                              <span className="txt-danger">*</span>
                            </Label>
                            <Field
                              id="passwordOption"
                              as="select"
                              name="passwordOption"
                              value={passwordOption}
                              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                handlePasswordOptionChange(e, setFieldValue)
                              }
                              className="form-control"
                            >
                              <option value="setOwn">{t("SetCustomPassword")}</option>
                              <option value="autoGenerate">{t("AutoGenerate")}</option>
                              <option value="invitationMail">{t("InvitationMail")}</option>
                            </Field>
                            <ErrorMessage
                              name="passwordOption"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm="6">
                          <FormGroup className="mb-0 mt-1">
                            {passwordOption === "setOwn" && (
                              <>
                                <Label for="password" className="mb-1">
                                  {t("EnterCustomPassword")}
                                </Label>
                                <Field
                                  id="password"
                                  name="password"
                                  type="password"
                                  className="form-control"
                                  placeholder={t("EnterPassword")}
                                  value={password}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setPassword(e.target.value);
                                    setFieldValue("password", e.target.value);
                                  }}
                                />
                                <ErrorMessage
                                  name="password"
                                  component="span"
                                  className="text-danger"
                                />
                              </>
                            )}
                            {passwordOption === "autoGenerate" && (
                              <>
                                <Label className="mb-1">
                                  {t("AutoGeneratedPassword")}
                                </Label>
                                <div style={{ position: "relative" }}>
                                  <Input
                                    type="password"
                                    className="form-control pe-5"
                                    value={password}
                                    readOnly
                                    ref={passwordInputRef}
                                  />
                                  <button
                                    type="button"
                                    style={{
                                      position: "absolute",
                                      top: "50%",
                                      right: 10,
                                      transform: "translateY(-50%)",
                                      border: "none",
                                      background: "none",
                                      padding: 0,
                                      cursor: "pointer",
                                    }}
                                    onClick={handleCopyPassword}
                                    tabIndex={-1}
                                    aria-label="Copy password"
                                  >
                                    <i className="fa fa-copy"></i>
                                    {copyTooltip && (
                                      <span
                                        style={{
                                          position: "absolute",
                                          top: "-30px",
                                          right: 0,
                                          backgroundColor: "#333",
                                          color: "#fff",
                                          padding: "2px 6px",
                                          borderRadius: "4px",
                                          fontSize: "12px",
                                          zIndex: 10,
                                        }}
                                      >
                                        {copyTooltip}
                                      </span>
                                    )}
                                  </button>
                                </div>
                              </>
                            )}
                            {passwordOption === "invitationMail" && (
                              <>
                                <Label className="mb-1">
                                  {t("InvitationLink")}
                                </Label>
                                <Input
                                  type="text"
                                  className="form-control"
                                  value="Invitation link will be sent to email"
                                  readOnly
                                  disabled
                                />
                              </>
                            )}
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-3">
                        <Col sm="3">
                          <FormGroup>
                            <Label for="gender" check>
                              {t("Gender")}
                            </Label>
                            <Field
                              id="gender"
                              as="select"
                              name="gender"
                              className="form-control flag-select"
                              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                setFieldValue("gender", e.target.value)
                              }
                              value={values.gender}
                            >
                              <option value="">{t("SelectGender")}</option>
                              {genderOptions.map((option) => (
                                <option
                                  key={option.value}
                                  value={option.value}
                                  data-flag={option.flag}
                                >
                                  {option.name}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name="gender"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm="3">
                          <FormGroup className="d-flex flex-column align-items-stretch">
                            <Label for="dob" check>
                              {t("DateOfBirth")}
                            </Label>
                            <ReactDatePicker
                              id="dob"
                              className="datepicker-here form-control"
                              selected={values.dob}
                              onChange={(date: Date | null) =>
                                setFieldValue("dob", date)
                              }
                              placeholderText={t("SelectDOB")}
                            />
                            <ErrorMessage
                              name="dob"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm="3">
                          <FormGroup>
                            <Label for="nationality" check>
                              {t("Nationality")}
                            </Label>
                            <Typeahead
                              id="nationality-typeahead"
                              labelKey="name"
                              options={nationalityOptions as FlagOption[]}
                              placeholder={t("SelectNationality")}
                              selected={nationalityOptions.filter(opt => values.nationality.includes(opt.value)) as FlagOption[]}
                              onChange={selected => {
                                const arr = selected.filter(sel => typeof sel === 'object' && 'value' in sel) as FlagOption[];
                                setFieldValue("nationality", arr.map(opt => opt.value));
                              }}
                              renderMenuItemChildren={option => {
                                const opt = option as FlagOption;
                                return (
                                  <div className="d-flex align-items-center">
                                    {opt.flag && (
                                      <img
                                        src={`/assets/fonts/flag-icon/${opt.flag}.svg`}
                                        alt={opt.name}
                                        style={{ width: 20, height: 15, marginRight: 8 }}
                                      />
                                    )}
                                    <span>{opt.name}</span>
                                  </div>
                                );
                              }}
                              renderToken={(option, props) => {
                                const opt = option as FlagOption;
                                return (
                                  <span {...props}>
                                    {opt.flag && (
                                      <img
                                        src={`/assets/fonts/flag-icon/${opt.flag}.svg`}
                                        alt={opt.name}
                                        style={{ width: 20, height: 15, marginRight: 4 }}
                                      />
                                    )}
                                    {opt.name}
                                  </span>
                                );
                              }}
                              renderInput={({ inputRef, referenceElementRef, ...inputProps }) => {
                                const selected = nationalityOptions.find((opt) =>
                                  values.nationality.includes(opt.value)
                                );
                                return (
                                  <div style={{ position: "relative" }}>
                                    {selected?.flag && (
                                      <img
                                        src={`/assets/fonts/flag-icon/${selected.flag}.svg`}
                                        alt={selected.name}
                                        style={{
                                          position: "absolute",
                                          left: 10,
                                          top: "50%",
                                          transform: "translateY(-50%)",
                                          width: 20,
                                          height: 15,
                                          pointerEvents: "none",
                                          zIndex: 2
                                        }}
                                      />
                                    )}
                                    <input
                                      {...inputProps}
                                      ref={input => {
                                        inputRef(input);
                                        referenceElementRef(input);
                                      }}
                                      className="form-control"
                                      style={{ paddingLeft: selected?.flag ? 35 : 12 }}
                                    />
                                  </div>
                                );
                              }}
                              multiple={false}
                            />
                            <ErrorMessage
                              name="nationality"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm="3">
                          <FormGroup>
                            <Label for="language" check>
                              {t("Language")}
                            </Label>
                            <Typeahead
                              id="language-typeahead"
                              labelKey="name"
                              options={languageOptions as FlagOption[]}
                              placeholder={t("SelectLanguage")}
                              selected={languageOptions.filter(opt => opt.value === values.language) as FlagOption[]}
                              onChange={selected => {
                                const sel = selected[0];
                                if (sel && typeof sel === 'object' && 'value' in sel) {
                                  setFieldValue("language", sel.value);
                                } else {
                                  setFieldValue("language", "");
                                }
                              }}
                              renderMenuItemChildren={option => {
                                const opt = option as FlagOption;
                                return (
                                  <div className="d-flex align-items-center">
                                    {opt.flag && (
                                      <img
                                        src={`/assets/fonts/flag-icon/${opt.flag}.svg`}
                                        alt={opt.name}
                                        style={{ width: 20, height: 15, marginRight: 8 }}
                                      />
                                    )}
                                    <span>{opt.name}</span>
                                  </div>
                                );
                              }}
                              renderToken={(option, props) => {
                                const opt = option as FlagOption;
                                return (
                                  <span {...props}>
                                    {opt.flag && (
                                      <img
                                        src={`/assets/fonts/flag-icon/${opt.flag}.svg`}
                                        alt={opt.name}
                                        style={{ width: 20, height: 15, marginRight: 4 }}
                                      />
                                    )}
                                    {opt.name}
                                  </span>
                                );
                              }}
                              renderInput={({ inputRef, referenceElementRef, ...inputProps }) => {
                                const selected = languageOptions.find(opt => opt.value === values.language);
                                return (
                                  <div style={{ position: "relative" }}>
                                    {selected?.flag && (
                                      <img
                                        src={`/assets/fonts/flag-icon/${selected.flag}.svg`}
                                        alt={selected.name}
                                        style={{
                                          position: "absolute",
                                          left: 10,
                                          top: "50%",
                                          transform: "translateY(-50%)",
                                          width: 20,
                                          height: 15,
                                          pointerEvents: "none",
                                          zIndex: 2
                                        }}
                                      />
                                    )}
                                    <input
                                      {...inputProps}
                                      ref={input => {
                                        inputRef(input);
                                        referenceElementRef(input);
                                      }}
                                      className="form-control"
                                      style={{ paddingLeft: selected?.flag ? 35 : 12 }}
                                    />
                                  </div>
                                );
                              }}
                              multiple={false}
                            />
                            <ErrorMessage
                              name="language"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-3">
                        <Col sm="12">
                          <FormGroup>
                            <Label for="image" check>
                              {t("Image")}
                            </Label>
                            <FilePond
                              files={files}
                              allowReorder={true}
                              allowMultiple={false}
                              maxFiles={1}
                              credits={false}
                              stylePanelLayout="compact"
                              onupdatefiles={(fileItems) => {
                                setFiles(
                                  fileItems.map((fileItem) => fileItem.file)
                                );
                                setFieldValue(
                                  "image",
                                  fileItems[0]?.file?.name || ""
                                );
                              }}
                              labelIdle={t("DragDropOrBrowse")}
                            />
                            <ErrorMessage
                              name="image"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-3">
                        <Col className="text-end">
                          <Button
                            onClick={handleClickCancel}
                            color="secondary"
                            className="me-2"
                          >
                            {t("Cancel")}
                          </Button>
                          <Button color="primary" type="submit">
                            {saveButtonText}
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AddDevelopersPage;